/**
 * Read-only school-pack rows for the chat agenda.
 * Opening is today and the next 6 days. «Was steht an?» is 60 days.
 * «Alle anzeigen» does not receive these rows.
 */
import { formatHeuteScheduleDate } from "./heuteScheduleBuckets";
import { itemShownOnCalendar, visiblePackDates } from "./occasionMatch";

/**
 * Same school or class occasion, whoever it was built for.
 * @param {object} date
 */
function packEventIdentity(date) {
  return `${date.packId || ""}|${date.kind}|${date.startsOn}|${date.endsOn}|${date.name}`;
}

/**
 * One row when siblings share a pack date. Per-child copies stay in the
 * source list so a saved Termin can still hide only one child's twin.
 * @param {object[]} dates
 */
function collapseSharedPackDates(dates) {
  const seen = new Set();
  const out = [];
  for (const date of dates || []) {
    const id = packEventIdentity(date);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(date);
  }
  return out;
}

/**
 * @param {object} date
 * @param {string} todayYmd
 */
export function schoolDateAgendaItem(date, todayYmd) {
  const startsOn = date.startsOn;
  const shown = startsOn < todayYmd ? todayYmd : startsOn;
  const packLabel = String(date.packLabel || "").trim();
  return {
    key: `school-${packEventIdentity(date)}`,
    kind: "school",
    origin: "school",
    readOnly: true,
    actionType: "event",
    isTask: false,
    isEvent: true,
    title: date.name,
    date: shown,
    endsOn: date.endsOn && date.endsOn !== shown ? date.endsOn : null,
    time: null,
    all_day: true,
    child: null,
    packLabel,
    kindLabel: date.kind === "school_closure" ? "schulfrei" : "",
    overdue: false,
    remindEnabled: false,
    id: null,
    packKind: date.kind,
    startsOn,
  };
}

/**
 * @param {object[]} dates
 * @param {string} from
 * @param {string} to
 */
export function schoolDatesInWindow(dates, from, to) {
  return (dates || []).filter((date) => date.startsOn <= to && date.endsOn >= from);
}

function coverItem(item) {
  return {
    title: item.title,
    date: item.date,
    endDate: item.endsOn || item.date,
    child: item.child,
    childId: item.childId,
    match_key: item.match_key || item.rawEvent?.match_key,
  };
}

/**
 * @param {object[]} saved normalized agenda rows
 * @param {object[]} schoolDates
 * @param {{ from: string, to: string, todayYmd: string, hideCalendarTwins?: boolean, calendarEvents?: object[] }} opts
 */
export function mergeSchoolDatesForAgenda(saved, schoolDates, opts) {
  const list = Array.isArray(saved) ? saved : [];
  const inWindow = schoolDatesInWindow(schoolDates, opts.from, opts.to);
  const visible = collapseSharedPackDates(visiblePackDates(inWindow, list.map(coverItem)));
  const rows = visible.map((date) => schoolDateAgendaItem(date, opts.todayYmd));
  const kept = opts.hideCalendarTwins
    ? list.filter((item) => !itemShownOnCalendar(coverItem(item), opts.calendarEvents))
    : list;
  return [...kept, ...rows].sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.date !== b.date) return String(a.date).localeCompare(String(b.date));
    return String(a.title || "").localeCompare(String(b.title || ""));
  });
}

/**
 * @param {object} item
 * @param {string} [todayYmd]
 */
export function formatSchoolAgendaMeta(item, todayYmd) {
  const start = formatHeuteScheduleDate(item.date, todayYmd);
  const end =
    item.endsOn && item.endsOn !== item.date ? formatHeuteScheduleDate(item.endsOn, todayYmd) : "";
  const when = end ? `${start} – ${end}` : start;
  return [when, item.kindLabel, item.packLabel].filter(Boolean).join(" · ");
}
